import { clone } from '../../../webmodule-common/other/clone.js';
import { ContainerManager } from '../../../webmodule-common/other/container-manager.js';
import { DevelopmentError } from '../../../webmodule-common/other/development-error.js';
import { emptyGuid, newGuid } from '../../../webmodule-common/other/api/guid.js';
import { EventSnippet, NullPromise } from '../../../webmodule-common/other/ui/events.js';
import { glossary } from '../../../localization/global.js';
import { localDateToServer, today } from '../../../webmodule-common/other/datetime-converter.js';
import { PaymentProfile, PaymentProfileCalculation } from '../../api/dealer-api-interface-franchisee.js';
import { PaymentProfileApi } from '../../api/payment-profile-api.js';
import { userDataStore } from '../../dealer-franchisee/common/current-user-data-store.js';

export class PaymentProfileContainer {
  paymentProfileId: string;
  paymentProfile: PaymentProfile | null;

  constructor(paymentProfileId: string, paymentProfile: PaymentProfile | null) {
    this.paymentProfileId = paymentProfileId;
    this.paymentProfile = paymentProfile;
  }
}

export class PaymentProfileContainerManager extends ContainerManager<PaymentProfileContainer> {
  protected paymentProfileApi: PaymentProfileApi;

  constructor(container: PaymentProfileContainer, title: EventSnippet, paymentProfileApi: PaymentProfileApi) {
    super(container, title, glossary('payment-profile'));
    this.paymentProfileApi = paymentProfileApi;
  }

  /**
   * the id for this managed container
   */
  public get paymentProfileId(): string {
    return this.container.paymentProfileId;
  }

  /**
   * returns the paymentProfile object after needsPaymentProfile is called, or throws an error if the paymentProfile is unavailable.
   */
  public get paymentProfile(): PaymentProfile {
    if (!this.container.paymentProfile) {
      throw new DevelopmentError(`payment-profile-container-manager.ts paymentProfile is null`);
    }
    return this.container.paymentProfile;
  }

  public async needsPaymentProfile() {
    if (!this.container.paymentProfile) {
      const result = await this.paymentProfileApi.getPaymentProfile({
        paymentProfileId: this.paymentProfileId
      });
      if (result && result.paymentProfile) {
        this.resetPaymentProfile(result.paymentProfile);
      } else return false;
    }
    return true;
  }

  /**
   * Override this to customize the save logic.
   * @returns True if the save operations have succeeded, false otherwise.
   */
  protected async internalSave(): Promise<boolean> {
    if (this.paymentProfileId == emptyGuid) {
      this.paymentProfile.id = newGuid();
      this.paymentProfile.value = this.paymentProfile.value || 0;
      const result = await this.paymentProfileApi.createPaymentProfile({
        paymentProfile: this.paymentProfile
      });
      if (result && result.paymentProfile) {
        this.resetPaymentProfile(result.paymentProfile);
        return true;
      }
    } else {
      const result = await this.paymentProfileApi.updatePaymentProfile({
        paymentProfile: this.paymentProfile
      });
      if (result && result.paymentProfile) {
        this.resetPaymentProfile(result.paymentProfile);
        return true;
      }
    }
    return false;
  }

  public resetBackup() {
    this.backup.paymentProfile = clone(this.container.paymentProfile);
    this.backup.paymentProfileId = this.container.paymentProfileId;
  }
  private resetPaymentProfile(paymentProfile: PaymentProfile) {
    this.container.paymentProfile = paymentProfile;
    this.container.paymentProfileId = this.container.paymentProfile.id;
    this.backup.paymentProfile = clone(paymentProfile);
    this.backup.paymentProfileId = this.backup.paymentProfile.id;
  }
}

export async function createNewPaymentProfile(): NullPromise<PaymentProfileContainer> {
  const paymentProfile: PaymentProfile = {
    id: emptyGuid,
    dateCreated: localDateToServer(today()),
    name: '',
    recordVersion: '',
    supplierId: emptyGuid,
    franchiseeId: userDataStore.franchisee.id,
    branchId: userDataStore.defaultBranch.id,
    value: 0,
    discount: 0,
    calculation: PaymentProfileCalculation.Margin
  };
  return new PaymentProfileContainer(emptyGuid, paymentProfile);
}
